import React, { useEffect, useState } from "react";
import { CloudSun, Warning, Wind, Drop } from "@phosphor-icons/react";
import { fetchWeatherOutlook } from "../lib/api";

const fmtHr = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric" });
const fmtDayHr = (iso) =>
  new Date(iso).toLocaleString([], { weekday: "short", hour: "numeric" });

function windowLabel(w) {
  const s = new Date(w.start);
  const e = new Date(w.end);
  e.setHours(e.getHours() + 1);
  const sameDay = s.toDateString() === e.toDateString();
  const day = s.toLocaleDateString([], { weekday: "short" });
  const sh = s.toLocaleTimeString([], { hour: "numeric" });
  const eh = e.toLocaleTimeString([], { hour: "numeric" });
  return sameDay ? `${day} ${sh}–${eh}` : `${fmtDayHr(w.start)} – ${fmtDayHr(e.toISOString())}`;
}

function Chart({ hours, riskWindows }) {
  const W = 480, H = 90, PAD = 4;
  const n = hours.length;
  const x = (i) => PAD + (i / (n - 1)) * (W - PAD * 2);
  const maxWind = Math.max(20, ...hours.map((h) => h.wind_mph || 0));
  const yWind = (v) => H - PAD - ((v || 0) / maxWind) * (H - PAD * 2);
  const yRh = (v) => H - PAD - ((v || 0) / 100) * (H - PAD * 2);
  const windPts = hours.map((h, i) => `${x(i)},${yWind(h.wind_mph)}`).join(" ");
  const rhPts = hours.map((h, i) => `${x(i)},${yRh(h.humidity)}`).join(" ");
  const idxOf = (iso) => hours.findIndex((h) => h.time === iso);
  const dayTicks = hours
    .map((h, i) => ({ h, i }))
    .filter(({ h }) => new Date(h.time).getHours() === 0 && new Date(h.time).getMinutes() === 0);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[90px]" data-testid="outlook-chart">
      {riskWindows.map((w, k) => {
        const a = idxOf(w.start), b = idxOf(w.end);
        if (a < 0) return null;
        return (
          <rect key={k} x={x(a)} y={PAD} width={Math.max(x(b < 0 ? a : b) - x(a), 3)}
            height={H - PAD * 2} fill="#E63946" opacity="0.18" />
        );
      })}
      {dayTicks.map(({ h, i }) => (
        <g key={h.time}>
          <line x1={x(i)} y1={PAD} x2={x(i)} y2={H - PAD} stroke="#3A423E" strokeWidth="1" strokeDasharray="3 3" />
          <text x={x(i) + 3} y={12} fill="#79837C" fontSize="9" fontFamily="monospace">
            {new Date(h.time).toLocaleDateString([], { weekday: "short" })}
          </text>
        </g>
      ))}
      <polyline points={rhPts} fill="none" stroke="#4A9BD1" strokeWidth="1.6" opacity="0.85" />
      <polyline points={windPts} fill="none" stroke="#F77F00" strokeWidth="2" />
    </svg>
  );
}

export default function FireWeatherOutlook({ lat, lon }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (lat == null || lon == null) return;
    fetchWeatherOutlook(lat, lon).then(setData).catch(() => setData({ available: false }));
  }, [lat, lon]);

  if (!data?.available || !data.hours?.length) return null;
  const risk = data.risk_windows || [];
  const shifts = data.wind_shifts || [];

  return (
    <div className="border border-tac-border bg-tac-surface/40 p-4" data-testid="fire-weather-outlook">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <CloudSun size={14} weight="duotone" className="text-tac-info" />
          <span className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint">
            48-Hour Fire Weather Outlook
          </span>
        </div>
        {data.near && <span className="text-[12px] font-mono-tac text-tac-faint">near {data.near}</span>}
      </div>

      {risk.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="outlook-risk-windows">
          {risk.map((w, i) => (
            <span key={i}
              className="inline-flex items-center gap-1 px-2 py-0.5 border border-tac-danger/60 bg-tac-danger/10 text-tac-danger text-[11px] font-bold uppercase tracking-wider">
              <Warning size={11} weight="fill" />
              {windowLabel(w)} · RH {w.min_rh}% · wind {Math.round(w.max_wind)} mph
            </span>
          ))}
        </div>
      ) : (
        <div className="text-[12px] font-mono-tac text-tac-dim mb-2" data-testid="outlook-no-risk">
          No Red Flag-risk hours (RH ≤ 25% + wind ≥ 15 mph) in the next 48 hours.
        </div>
      )}

      <Chart hours={data.hours} riskWindows={risk} />

      <div className="grid grid-cols-3 gap-2 mt-2 text-center">
        <div className="border border-tac-border/60 py-1.5" data-testid="outlook-peak-wind">
          <Wind size={11} className="inline text-tac-primary mr-1" />
          <span className="text-sm font-bold text-tac-text tabular">
            {data.peak_wind ? `${Math.round(data.peak_wind.wind_mph)} mph` : "—"}
          </span>
          <div className="text-[10px] font-mono-tac text-tac-faint">
            peak {data.peak_wind ? `${data.peak_wind.wind_dir || ""} ${fmtDayHr(data.peak_wind.time)}` : ""}
          </div>
        </div>
        <div className="border border-tac-border/60 py-1.5" data-testid="outlook-min-rh">
          <Drop size={11} className="inline text-tac-info mr-1" />
          <span className="text-sm font-bold text-tac-text tabular">
            {data.min_rh ? `${data.min_rh.humidity}% RH` : "—"}
          </span>
          <div className="text-[10px] font-mono-tac text-tac-faint">
            driest {data.min_rh ? fmtDayHr(data.min_rh.time) : ""}
          </div>
        </div>
        <div className="border border-tac-border/60 py-1.5" data-testid="outlook-rh-recovery">
          <span className="text-sm font-bold text-tac-text tabular">
            {data.overnight_rh_recovery != null ? `${data.overnight_rh_recovery}%` : "—"}
          </span>
          <div className="text-[10px] font-mono-tac text-tac-faint">overnight RH recovery</div>
        </div>
      </div>

      {shifts.length > 0 && (
        <div className="text-[12px] font-mono-tac text-tac-dim mt-2" data-testid="outlook-wind-shifts">
          {shifts.map((s, i) => (
            <div key={i}>
              ⚠ Wind shift {s.from_dir} → {s.to_dir} around {fmtDayHr(s.time)} ({Math.round(s.wind_mph)} mph)
            </div>
          ))}
        </div>
      )}

      <div className="text-[11px] font-mono-tac text-tac-faint mt-2">
        <span className="text-[#F77F00]">━ wind mph</span> · <span className="text-[#4A9BD1]">━ humidity %</span> · red bands = Red Flag-risk hours · NWS/NBM forecast, updated hourly
      </div>
    </div>
  );
}
